import { Fragment } from "react";
import styled from "styled-components";

import { adminRoutePaths } from "@pages/AdminDashboard/adminRoutes";
import { carrierTrackingUrl } from "@pages/AdminDashboard/externalLinks";
import { InvoiceLink } from "@pages/AdminDashboard/InvoiceLink";
import { OpenInButtons } from "@pages/AdminDashboard/OpenInButtons";
import { orderStatusLabel, shippingMethodLabel } from "@pages/AdminDashboard/plainLanguageLabels";
import { telehealthCaseUrl } from "@pages/AdminDashboard/telehealthLinks";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";
import { CopyButton } from "@pages/AdminDashboard/ui/CopyButton";
import { DataTableColumn } from "@pages/AdminDashboard/ui/DataTable";
import {
  ExternalLink,
  IdentifierCell,
  ScreenLinkButton,
  ShortIdentifier,
  shortenIdentifier,
} from "@pages/AdminDashboard/ui/DataTable/cells";
import { StatusBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { Order } from "@types-runway/case";

const { color, typeScale } = adminTokens;
const [, , spaceMd] = adminTokens.spacing;
const linkFontSize = "13px";
const noValue = "—";

interface OrderPatient {
  email: string;
  first_name?: string;
  last_name?: string;
}

// The search response carries the paid flag of the order (orders-api#Order response shape) and,
// for an admin, the patient on its Case (ENG-1111, contract, mocked).
export type FoundOrder = Omit<Order, "Case"> & {
  paid?: boolean;
  Case?: (Order["Case"] & { User?: OrderPatient | null }) | null;
};

// The email of the Order's patient, or null when the Order's Case carries no User (FIND-R27).
export const findOrderPatientEmail = (order: FoundOrder): string | null =>
  order.Case?.User?.email ?? null;

const IdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Secondary = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: 12px;
`;

// An order ID and a tracking number are long and may need to break inside a narrow column
// (the property is inherited by whatever the cell holds).
// A short identifier is eight characters and is not broken.
const NoBreak = styled.div`
  white-space: nowrap;
`;

const Breakable = styled.div`
  overflow-wrap: anywhere;
`;

// A method with no plain-language label, such as dhl_express_worldwide, breaks after an
// underscore, never inside a word.
const withBreakPointsAfterUnderscores = (value: string) =>
  value.split("_").map((part, index) => (
    <Fragment key={`${part}-${index}`}>
      {index > 0 && (
        <>
          _<wbr />
        </>
      )}
      {part}
    </Fragment>
  ));

const describeShippingMethod = (shippingMethod: string) => {
  const label = shippingMethodLabel(shippingMethod);

  return label === shippingMethod ? withBreakPointsAfterUnderscores(shippingMethod) : label;
};

const OpenIn = styled.div`
  display: flex;
  gap: ${spaceMd}px;
  white-space: nowrap;
`;

const OpenInLink = styled.a`
  color: ${color.tealDark};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${linkFontSize};

  &:focus-visible {
    ${focusOutline}
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  overflow-wrap: anywhere;
`;

const describeTracking = ({ carrier, tracking_number: trackingNumber }: FoundOrder) =>
  trackingNumber ? `${carrier ? `${carrier.toUpperCase()} · ` : ""}${trackingNumber}` : noValue;

// The tracking number opens the shipment at its carrier when the carrier has a tracking page
// (XCUT-R24, R25).
const TrackingCell = ({ order }: { order: FoundOrder }) => {
  const trackingUrl = order.tracking_number
    ? carrierTrackingUrl(order.carrier, order.tracking_number)
    : null;

  return (
    <Breakable data-testid="order-tracking">
      {trackingUrl ? (
        <ExternalLink href={trackingUrl}>{describeTracking(order)}</ExternalLink>
      ) : (
        describeTracking(order)
      )}
    </Breakable>
  );
};

const describeShipsTo = ({ Case: orderCase }: FoundOrder) => {
  const address = orderCase?.shippingAddress;

  return address ? `${address.city}, ${address.state}` : noValue;
};

const openInLink = (path: string, caseId: string | null | undefined) =>
  `${path}?case=${encodeURIComponent(caseId ?? "")}`;

// On a phone the links become the 44px secondary Buttons a finger can hit (FIND-R22).
const OrderOpenIn = ({ caseId }: { caseId: string | null | undefined }) => {
  const isPhone = useIsPhoneLayout();

  if (isPhone) return <OpenInButtons caseId={caseId} />;

  return (
    <OpenIn>
      <OpenInLink href={openInLink(adminRoutePaths.redo, caseId)}>Redo</OpenInLink>
      <OpenInLink href={openInLink(adminRoutePaths.ship, caseId)}>Ship</OpenInLink>
    </OpenIn>
  );
};

interface OrderIdProps {
  order: FoundOrder;
  onOpenAccount: (order: FoundOrder) => void;
}

// The first segment of the order ID opens the account, with the whole ID on hover and its copy
// Button beside it; an Order whose User is not known shows it as text (ORDERS-R9, FIND-R27).
const OrderId = ({ order, onOpenAccount }: OrderIdProps) => {
  const shortId = shortenIdentifier(order.order_id);

  return (
    <IdRow>
      {findOrderPatientEmail(order) ? (
        <ScreenLinkButton
          name={`Open account for ${shortId}`}
          title={order.order_id}
          onClick={() => onOpenAccount(order)}
        >
          {shortId}
        </ScreenLinkButton>
      ) : (
        <ShortIdentifier value={order.order_id} />
      )}
      <CopyButton value={order.order_id} what="order ID" />
    </IdRow>
  );
};

export interface OrderColumnHandlers {
  onOpenAccount: (order: FoundOrder) => void;
}

// FIND-E22: one order per row — status, shipping method, tracking, paid flag, the linked case
// and where it ships. Cancelling, replacing and shipping changes are the Redo and Ship
// screens' jobs, reached by the Open in links (ORDERS-R8…R17).
export const buildOrderColumns = ({
  onOpenAccount,
}: OrderColumnHandlers): DataTableColumn<FoundOrder>[] => [
  {
    id: "order",
    header: "Order",
    render: (order) => (
      <NoBreak>
        <IdentifierCell
          primary={<OrderId order={order} onOpenAccount={onOpenAccount} />}
          secondary={`#${order.id}`}
        />
      </NoBreak>
    ),
  },
  {
    id: "case",
    header: "Case",
    render: ({ Case: orderCase, caseId }) => (
      <Stack>
        <span>{orderCase?.id === undefined ? noValue : `#${orderCase.id}`}</span>
        <Secondary data-testid="order-case-id">
          {caseId ? (
            <ExternalLink href={telehealthCaseUrl(caseId)} title={caseId}>
              {shortenIdentifier(caseId)}
            </ExternalLink>
          ) : (
            noValue
          )}
        </Secondary>
      </Stack>
    ),
  },
  {
    id: "status",
    header: "Status",
    phonePlacement: "badge",
    render: ({ status }) =>
      status ? (
        <StatusBadge status={status} label={orderStatusLabel(status)} />
      ) : (
        <span data-testid="order-status">{noValue}</span>
      ),
  },
  {
    id: "shippingMethod",
    header: "Shipping method",
    render: ({ shipping_method: shippingMethod }) =>
      shippingMethod ? describeShippingMethod(shippingMethod) : noValue,
  },
  {
    id: "tracking",
    header: "Tracking",
    render: (order) => <TrackingCell order={order} />,
  },
  {
    id: "paid",
    header: "Paid",
    render: ({ paid, invoiceId }) => (
      <InvoiceLink invoiceId={invoiceId}>{paid ? "Paid" : "Unpaid"}</InvoiceLink>
    ),
  },
  {
    id: "shipsTo",
    header: "Ships to",
    render: (order) => <span data-testid="order-ships-to">{describeShipsTo(order)}</span>,
  },
  {
    id: "openIn",
    header: "Open in",
    isHeaderHidden: true,
    render: ({ caseId }) => <OrderOpenIn caseId={caseId} />,
  },
];
